import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../../styles/designTokens";

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;
const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }

  .content-wrapper {
    width: 100%;
    max-width: 90em;
    margin: 0 auto;
    padding: 2em;
    display: flex;
    flex-direction: column;
    gap: 14px;
    height: 100%;
    min-height: 0;

    @media (max-width: 768px) { padding: 1em; }
  }

  /* Botão Voltar */
  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    margin-bottom: 8px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    font-size: 0.78em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    ${mono};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    width: fit-content;

    &:hover {
      opacity: 0.85;
      background: ${({ theme }) => surface(theme, 1)};
    }

    svg { font-size: 0.95em; }
  }

  /* ====== HEADER DO PRODUTO ====== */
  .product-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 0 0 8px 0;
    position: relative;

    .product-info-bar {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 16px;
      min-width: 0;

      .product-icon {
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${tokens.brandSoft};
        border-radius: ${tokens.radiusLg};
        flex-shrink: 0;

        svg {
          font-size: 22px;
          color: ${tokens.brand};
        }
      }

      .product-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
        
        h2 {
          font-size: 1.6em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          margin: 0;
          letter-spacing: -0.025em;
          line-height: 1.15;
        }

        .product-eyebrow {
          ${eyebrow};
          margin-bottom: 2px;
        }

        .product-badges {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;

          .badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            background: ${({ theme }) => surface(theme, 1)};
            border: 1px solid ${({ theme }) => hairline(theme)};
            border-radius: ${tokens.radiusMd};
            font-size: 0.72em;
            color: ${({ theme }) => theme.text};
            white-space: nowrap;
            transition: all ${tokens.durBase} ${tokens.easeOut};

            strong {
              font-weight: 600;
              color: ${tokens.brand};
              font-size: 0.9em;
              text-transform: uppercase;
              letter-spacing: 0.04em;
            }

            &:hover {
              border-color: ${tokens.brandRing};
            }
          }
        }
      }
    }
  }

  /* ====== HEADER CLIENTE (fallback) ====== */
  .client-header {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
    ${({ theme }) => cardBase(theme)};

    .client-info {
      display: flex;
      align-items: flex-start;
      gap: 16px;

      .client-icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${tokens.brandSoft};
        border-radius: ${tokens.radiusLg};
        flex-shrink: 0;

        svg {
          font-size: 22px;
          color: ${tokens.brand};
        }
      }

      .client-details {
        flex: 1;

        h2 {
          font-size: 1.4em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          margin: 0 0 4px 0;
          letter-spacing: -0.02em;
        }

        .razao-social {
          font-size: 0.82em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin: 0 0 10px 0;
        }

        .client-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-top: 6px;

          .meta-item {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 0.72em;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            ${mono};

            strong {
              font-weight: 500;
              opacity: 0.7;
            }

            svg {
              font-size: 0.9em;
              color: ${tokens.brand};
              opacity: 0.7;
            }
          }
        }
      }
    }
  }

  /* ====== KPI GRID ====== */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;

    &.kpi-grid-5 { grid-template-columns: repeat(5, 1fr); }
    &.kpi-grid-4 { grid-template-columns: repeat(4, 1fr); }
    &.kpi-grid-3 { grid-template-columns: repeat(3, 1fr); }
    &.kpi-grid-2 { grid-template-columns: repeat(2, 1fr); }

    @media (max-width: 1400px) {
      grid-template-columns: repeat(3, 1fr);
      &.kpi-grid-5 { grid-template-columns: repeat(3, 1fr); }
      &.kpi-grid-4 { grid-template-columns: repeat(2, 1fr); }
      &.kpi-grid-3 { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      &.kpi-grid-5,
      &.kpi-grid-4,
      &.kpi-grid-3,
      &.kpi-grid-2 { grid-template-columns: 1fr; }
    }

    .kpi-card {
      ${({ theme }) => cardBase(theme)};
      padding: 16px 18px 14px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 96px;
      position: relative;
      overflow: hidden;

      &:hover {
        border-color: ${({ theme }) => hairline(theme, true)};
        background: ${({ theme }) => surface(theme, 1)};
      }

      .card-content {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .thumb {
          opacity: 0.35;
          font-size: 0.9em;
          color: ${({ theme }) => theme.text};
          display: inline-flex;
          align-items: center;
        }

        .text {
          display: flex;
          flex-direction: column;
          gap: 2px;

          p { margin: 0; }

          p:first-child {
            ${mono};
            font-size: 1.5em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            letter-spacing: -0.02em;
            line-height: 1.1;
          }

          p:last-child {
            font-size: 0.72em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            margin-top: 2px;
            letter-spacing: -0.005em;
          }
        }
      }

      &.primary .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
      &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
      &.warning .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
      &.info    .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
      &.tax-cbs .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
      &.tax-ibs .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    }
  }

  /* Seção Row */
  .section-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    align-items: start;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  /* Stats Card */
  .stats-card {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    min-height: max-content;
    height: fit-content;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 18px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .header-title {
        display: flex;
        align-items: center;
        gap: 8px;

        svg {
          font-size: 0.95em;
          color: ${tokens.brand};
          opacity: 0.7;
        }

        h4 {
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          margin: 0;
          letter-spacing: -0.01em;
        }
      }

      .header-badge {
        font-size: 0.6em;
        font-weight: 600;
        color: ${tokens.brand};
        background: ${tokens.brandSoft};
        padding: 3px 8px;
        border-radius: ${tokens.radiusSm};
        text-transform: uppercase;
        letter-spacing: 0.04em;
        ${mono};
      }

      .chart-legend {
        display: flex;
        gap: 16px;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;

          .legend-color {
            width: 10px;
            height: 10px;
            border-radius: 3px;
          }
        }
      }
    }
  }

  /* Contact Card e Metrics Card - Mesma altura */
  .contact-card,
  .metrics-card {
    height: 220px;
    display: flex;
    flex-direction: column;
  }

  .contact-card {
    .contact-content {
      padding: 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      flex: 1;

      .contact-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;

        > svg {
          font-size: 18px;
          color: ${({ theme }) => theme.primary};
          margin-top: 2px;
          flex-shrink: 0;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .contact-label {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .contact-value {
            font-size: 13px;
            color: ${({ theme }) => theme.text};
            font-weight: 500;
          }

          .contact-detail {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* Metrics Card */
  .metrics-card {
    .metrics-content {
      padding: 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      flex: 1;

      .metric-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 0;
        border-bottom: 1px solid ${({ theme }) => theme.border}20;

        &:last-child {
          border-bottom: none;
        }

        .metric-label {
          font-size: 12px;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
        }

        .metric-value {
          font-size: 13px;
          font-weight: 600;
          color: ${({ theme }) => theme.text};

          &.highlight {
            color: ${({ theme }) => theme.primary};
          }
        }
      }
    }
  }

  /* Chart Card */
  .chart-card {
    .chart-container {
      padding: 16px;

      &.pie {
        display: flex;
        align-items: center;
        gap: 20px;

        .pie-legend {
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-width: 140px;

          .pie-legend-item {
            display: flex;
            align-items: center;
            gap: 8px;

            .legend-dot {
              width: 10px;
              height: 10px;
              border-radius: 3px;
              flex-shrink: 0;
            }

            .legend-name {
              font-size: 12px;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
              flex: 1;
            }

            .legend-value {
              font-size: 12px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
            }
          }
        }
      }
    }
  }

  /* Products Card */
  .products-card {
    .products-list {
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .product-item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px 14px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          background: ${({ theme }) => theme.tableRowHover};
        }

        .product-rank {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${({ theme }) => theme.buttonBg};
          color: ${({ theme }) => theme.text};
          font-size: 11px;
          font-weight: 600;
          border-radius: 6px;
          opacity: 0.7;
          flex-shrink: 0;
        }

        .product-main {
          flex: 1;
          min-width: 0;

          .product-info {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 8px;

            .product-name {
              font-size: 13px;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .product-qty {
              font-size: 11px;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }
          }

          .product-bar {
            height: 3px;
            background: ${({ theme }) => theme.buttonBg};
            border-radius: 2px;
            overflow: hidden;

            .bar-fill {
              height: 100%;
              background: ${({ theme }) => theme.primary};
              border-radius: 2px;
              transition: width 0.5s ease;
              opacity: 0.7;
            }
          }
        }

        .product-value {
          font-size: 14px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          font-family: 'Inter', system-ui, sans-serif;
          flex-shrink: 0;
        }
      }
    }
  }

  /* Taxes Section */
  .taxes-section {
    .section-title {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 16px;

      svg {
        font-size: 20px;
        color: ${({ theme }) => theme.primary};
      }

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        margin: 0;
      }
    }

    .taxes-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }

      .tax-card {
        padding: 20px;
        background: ${({ theme }) => theme.card};
        border-radius: 12px;
        border: 1px solid ${({ theme }) => theme.border};
        border-left: 3px solid;

        &.icms {
          border-left-color: #3b82f6;
          .tax-name {
            color: #3b82f6;
          }
        }

        &.pis-cofins {
          border-left-color: #10b981;
          .tax-name {
            color: #10b981;
          }
        }

        &.ibs {
          border-left-color: #f59e0b;
          .tax-name {
            color: #f59e0b;
          }
        }

        &.cbs {
          border-left-color: #8b5cf6;
          .tax-name {
            color: #8b5cf6;
          }
        }

        .tax-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;

          .tax-name {
            font-size: 14px;
            font-weight: 700;
          }

          .tax-badge {
            font-size: 9px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            background: ${({ theme }) => theme.buttonBg};
            padding: 3px 8px;
            border-radius: 6px;
            text-transform: uppercase;
          }
        }

        .tax-value {
          font-size: 20px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          font-family: 'Inter', system-ui, sans-serif;
          margin-bottom: 6px;
        }

        .tax-percent {
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }
    }
  }

  /* Donut Chart Wrapper */
  .donut-wrapper {
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
    }

    .donut-chart {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;

      svg {
        display: block;
      }

      .donut-center {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 2px;

        .donut-total {
          font-size: 16px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          font-family: 'Inter', system-ui, sans-serif;
        }

        .donut-label {
          font-size: 10px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          text-transform: uppercase;
        }
      }
    }

    .categories-list {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 10px;

      .category-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;

        .category-color {
          width: 10px;
          height: 10px;
          border-radius: 3px;
          flex-shrink: 0;
        }

        .category-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;

          .category-name {
            font-size: 12px;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
          }

          .category-percent {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        .category-value {
          font-size: 13px;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          font-family: 'Inter', system-ui, sans-serif;
        }
      }
    }
  }

  /* Seção de Produtos com Gráfico e Tabela */
  .products-row {
    grid-template-columns: 1fr 1.5fr;
    align-items: stretch;

    .chart-card,
    .table-card {
      height: 480px;
      max-height: 480px;
      display: flex;
      flex-direction: column;
    }

    .chart-card {
      .chart-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .table-card {
      .table-container {
        flex: 1;
        overflow-y: auto;

        &::-webkit-scrollbar {
          width: 4px;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => theme.border};
          border-radius: 2px;
        }
      }
    }
  }

  /* Tabelas de Dados - Design Sofisticado */
  .table-card,
  .history-table-card {
    font-family: 'Inter', system-ui, sans-serif;

    .table-container {
      overflow-x: auto;

      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 3px;
      }
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;

      thead tr th {
        padding: 14px 16px;
        font-size: 11px;
        font-weight: 500;
        font-family: 'Inter', system-ui, sans-serif;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        text-align: left;
        background: ${({ theme }) => theme.tableRow};
        border-bottom: 1px solid ${({ theme }) => theme.border}20;
        white-space: nowrap;
      }

      tbody tr.data-row {
        transition: background 0.15s ease;

        &:hover {
          background: ${({ theme }) => theme.tableRowHover};
        }

        td {
          padding: 14px 16px;
          font-size: 13px;
          font-family: 'Inter', system-ui, sans-serif;
          color: ${({ theme }) => theme.text};
          border-bottom: 1px solid ${({ theme }) => theme.border}20;
          vertical-align: middle;

          .rank-number {
            font-size: 12px;
            font-weight: 500;
            font-family: 'Inter', system-ui, sans-serif;
            color: ${({ theme }) => theme.text};
            opacity: 0.4;
          }

          .produto-cell {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .produto-nome {
              font-weight: 500;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              font-size: 13px;
              line-height: 1.3;
            }

            .produto-setor {
              font-size: 11px;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              opacity: 0.45;
              font-weight: 400;
            }
          }

          .doc-cell {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .doc-nf {
              font-weight: 500;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              font-size: 13px;
            }

            .doc-municipio {
              font-size: 11px;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              opacity: 0.45;
            }
          }

          .cfop-cell {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .cfop-code {
              font-weight: 600;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              font-size: 13px;
            }

            .cfop-desc {
              font-size: 10px;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              opacity: 0.45;
              max-width: 120px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }

          .ncm-value {
            font-family: 'Inter', system-ui, sans-serif;
            font-size: 12px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            font-weight: 400;
          }

          .uf-value {
            font-size: 13px;
            font-weight: 500;
            font-family: 'Inter', system-ui, sans-serif;
            color: ${({ theme }) => theme.text};
            opacity: 0.8;
          }

          .date-value {
            font-size: 12px;
            font-family: 'Inter', system-ui, sans-serif;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .qty-value {
            font-weight: 500;
            font-family: 'Inter', system-ui, sans-serif;
            color: ${({ theme }) => theme.text};
            font-size: 13px;
          }

          .tax-value {
            font-weight: 400;
            font-family: 'Inter', system-ui, sans-serif;
            font-size: 12px;
            color: ${({ theme }) => theme.text};
            opacity: 0.65;
          }

          .aliquota-value {
            font-size: 12px;
            font-weight: 400;
            font-family: 'Inter', system-ui, sans-serif;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .valor-destaque {
            font-weight: 600;
            font-family: 'Inter', system-ui, sans-serif;
            font-size: 14px;
            color: ${({ theme }) => theme.primary};
          }

          .participacao-bar-container {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 80px;

            .participacao-bar {
              height: 3px;
              border-radius: 2px;
              min-width: 4px;
              background: ${({ theme }) => theme.primary};
              opacity: 0.5;
            }

            .participacao-value {
              font-size: 12px;
              font-weight: 500;
              font-family: 'Inter', system-ui, sans-serif;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
            }
          }
        }
      }
    }
  }

  .history-table-card {
    .card-header {
      .header-metrics {
        display: flex;
        gap: 16px;

        .metric-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 10px 16px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 12px;
          border: 1px solid ${({ theme }) => theme.border};

          .metric-value {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            font-family: 'Inter', system-ui, sans-serif;
          }

          .metric-label {
            font-size: 10px;
            font-family: 'Inter', system-ui, sans-serif;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }
      }
    }
  }

  /* Tabela de Produtos (Clientes e Fornecedores) */
  .products-table {
    padding: 0;

    .table-header {
      display: grid;
      grid-template-columns: 2fr 1.2fr 1.2fr 1.5fr 0.8fr;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => theme.tableRow};
      border-bottom: 1px solid ${({ theme }) => theme.border};
      font-size: 11px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .table-row {
      display: grid;
      grid-template-columns: 2fr 1.2fr 1.2fr 1.5fr 0.8fr;
      gap: 12px;
      padding: 16px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.border}20;
      transition: all 0.2s ease;
      align-items: center;

      &:hover {
        background: ${({ theme }) => theme.tableRowHover};
      }

      &:last-child {
        border-bottom: none;
      }
    }

    .col-cliente,
    .col-quantidade,
    .col-valor,
    .col-participacao,
    .col-notas {
      display: flex;
      align-items: center;
    }

    .product-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .product-color {
        width: 8px;
        height: 8px;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .product-details {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;

        .product-name {
          font-size: 13px;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .product-code {
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }
    }

    .quantity-badge {
      font-size: 12px;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;
    }

    .value-text {
      font-size: 13px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }

    .participation-bar {
      position: relative;
      width: 100%;
      height: 24px;
      background: ${({ theme }) => theme.buttonBg};
      border-radius: 6px;
      overflow: hidden;

      .participation-fill {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        border-radius: 6px;
        transition: width 0.3s ease;
        opacity: 0.3;
      }

      .participation-text {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        font-size: 11px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }
    }

    .notes-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 32px;
      height: 24px;
      padding: 0 8px;
      background: ${({ theme }) => theme.buttonBg};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 6px;
      font-size: 11px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
    }
  }

  /* Tabs de Navegação */
  .tabs-container {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;
      outline: none;

      svg { font-size: 0.95em; }

      &:hover { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }
    }
  }

  /* Timeline da Reforma Tributária */
  .reform-timeline {
    display: flex !important;
    gap: 8px;
    padding: 0;
    margin-bottom: 12px;
    overflow-x: auto;
    width: 100%;
    min-height: 56px;

    &::-webkit-scrollbar { height: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 2px;
    }

    .timeline-item {
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px 16px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      flex-shrink: 0;
      min-width: 100px;
      position: relative;
      outline: none;

      .timeline-year {
        display: block;
        font-size: 1.2em;
        font-weight: 600;
        font-family: 'Poppins', system-ui, sans-serif;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: -0.02em;
      }

      .timeline-label {
        display: block;
        font-size: 0.7em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        text-align: center;
        white-space: nowrap;
      }

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};

        .timeline-year { opacity: 0.85; }
        .timeline-label { opacity: 0.65; }
      }

      &.active {
        background: ${tokens.brandSoft};
        border: 1px solid ${tokens.brandRing};
        border-left: 3px solid ${tokens.brand};

        .timeline-year {
          color: ${tokens.brand};
          opacity: 1;
        }

        .timeline-label {
          color: ${({ theme }) => theme.text};
          opacity: 1;
        }
      }
    }
  }

  /* ========== REFORMA TRIBUTÁRIA TAB ========== */
  .reforma-tab {
    display: flex;
    flex-direction: column;
    gap: 16px;

    /* Cenário Selector */
    .cenario-selector {
      display: flex;
      gap: 6px;
      padding: 6px;
      background: ${({ theme }) => theme.card};
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.border};
      width: fit-content;

      .cenario-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        padding: 10px 20px;
        background: transparent;
        border: 1px solid transparent;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 80px;

        .cenario-year {
          font-size: 16px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
        }

        .cenario-label {
          font-size: 10px;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }

        &:hover {
          background: ${({ theme }) => theme.tableRowHover};

          .cenario-year, .cenario-label {
            opacity: 0.8;
          }
        }

        &.active {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: #3b82f6;

          .cenario-year {
            color: #fff;
            opacity: 1;
          }

          .cenario-label {
            color: rgba(255, 255, 255, 0.9);
            opacity: 1;
          }
        }
      }
    }

    /* KPIs Grid */
    .reforma-kpis {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }

      .kpi-card {
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        padding: 16px;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .card-content {
          display: flex;
          align-items: center;
          gap: 16px;

          .icon {
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${({ theme }) => theme.buttonBg};
            border-radius: 12px;

            svg {
              font-size: 22px;
            }
          }

          .text {
            flex: 1;

            .value {
              font-size: 22px;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: 'Inter', system-ui, sans-serif;
              margin-bottom: 4px;
            }

            .label {
              font-size: 13px;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
            }
          }
        }

        &.primary .icon { color: #3b82f6; background: #3b82f615; }
        &.warning .icon { color: #f59e0b; background: #f59e0b15; }
        &.success .icon { color: #10b981; background: #10b98115; }
        &.danger .icon { color: #ef4444; background: #ef444415; }
      }
    }

    /* Comparação Side by Side */
    .comparison-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;

      @media (max-width: 1000px) {
        grid-template-columns: 1fr;
      }
    }

    .comparison-card {
      background: ${({ theme }) => theme.card};
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.border};
      overflow: hidden;

      .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        .title {
          display: flex;
          align-items: center;
          gap: 10px;

          svg {
            font-size: 18px;
            color: ${({ theme }) => theme.primary};
          }

          h4 {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            margin: 0;
          }
        }

        .badge {
          font-size: 10px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 6px;
          text-transform: uppercase;

          &.atual {
            color: #6b7280;
            background: #6b728015;
          }

          &.futuro {
            color: #3b82f6;
            background: #3b82f615;
          }
        }
      }

      .card-body {
        padding: 14px 16px;
      }

      .tax-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        
        .tax-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 0;
          border-bottom: 1px solid ${({ theme }) => theme.border}15;

          &:last-child {
            border-bottom: none;
          }

          .tax-info {
            display: flex;
            align-items: center;
            gap: 10px;

            .tax-dot {
              width: 10px;
              height: 10px;
              border-radius: 3px;

              &.icms { background: #3b82f6; }
              &.pis { background: #10b981; }
              &.cofins { background: #f59e0b; }
              &.ipi { background: #8b5cf6; }
              &.cbs { background: #10b981; }
              &.ibs { background: #3b82f6; }
            }

            .tax-name {
              font-size: 13px;
              color: ${({ theme }) => theme.text};
            }

            .tax-rate {
              font-size: 11px;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
              margin-left: 6px;
            }
          }

          .tax-value {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            font-family: 'Inter', system-ui, sans-serif;
          }
        }

        .tax-total {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 0 0 0;
          margin-top: 4px;
          border-top: 1px solid ${({ theme }) => theme.border};

          .total-label {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }

          .total-value {
            font-size: 18px;
            font-weight: 700;
            font-family: 'Inter', system-ui, sans-serif;

            &.atual { color: #6b7280; }
            &.futuro { color: #3b82f6; }
          }
        }
      }
    }

    /* Chart Row */
    .chart-row {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 12px;

      @media (max-width: 1000px) {
        grid-template-columns: 1fr;
      }
    }

    .chart-card {
      background: ${({ theme }) => theme.card};
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.border};
      overflow: hidden;

      .chart-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        .chart-title {
          display: flex;
          align-items: center;
          gap: 10px;

          svg {
            font-size: 18px;
            color: ${({ theme }) => theme.primary};
          }

          span {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }
        }
      }

      .chart-body {
        padding: 20px;
      }
    }

    /* Impact Card */
    .impact-card {
      background: ${({ theme }) => theme.card};
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.border};
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;

      .impact-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .impact-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #3b82f615;
          border-radius: 12px;

          svg {
            font-size: 18px;
            color: #3b82f6;
          }
        }

        .impact-badge {
          font-size: 10px;
          font-weight: 700;
          padding: 4px 8px;
          border-radius: 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          
          &.aumento {
            color: #ef4444;
            background: #ef444415;
          }

          &.economia {
            color: #10b981;
            background: #10b98115;
          }
        }
      }

      .impact-value {
        text-align: center;

        .value {
          font-size: 28px;
          font-weight: 800;
          font-family: 'Inter', system-ui, sans-serif;
          color: ${({ theme }) => theme.text};
          margin-bottom: 2px;
        }

        .label {
          font-size: 12px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
        }
      }

      .impact-variation {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 8px;
        background: ${({ theme }) => theme.buttonBg};
        border-radius: 6px;

        svg {
          font-size: 16px;
        }

        .variation-value {
          font-size: 14px;
          font-weight: 700;
        }

        .variation-label {
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
        }

        &.aumento {
          svg, .variation-value { color: #ef4444; }
        }

        &.economia {
          svg, .variation-value { color: #10b981; }
        }
      }

      .impact-compare {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .compare-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid ${({ theme }) => theme.border}20;

          &:last-child {
            border-bottom: none;
          }

          .compare-label {
            font-size: 12px;
            color: ${({ theme }) => theme.text};
            opacity: 0.7;
          }

          .compare-value {
            font-size: 13px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }
        }
      }
    }

    /* Timeline Bar */
    .timeline-bar {
      background: ${({ theme }) => theme.card};
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.border};
      padding: 16px;

      .timeline-title {
        font-size: 13px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        margin-bottom: 14px;
      }

      .timeline-track {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 20px;

        &::before {
          content: '';
          position: absolute;
          left: 20px;
          right: 20px;
          top: 50%;
          height: 4px;
          background: ${({ theme }) => theme.border};
          transform: translateY(-50%);
          border-radius: 2px;
        }

        .timeline-point {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;

          .point-dot {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: ${({ theme }) => theme.buttonBg};
            border: 3px solid ${({ theme }) => theme.border};
            transition: all 0.2s ease;
          }

          .point-year {
            font-size: 12px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .point-info {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-align: center;
            line-height: 1.3;
          }

          &:hover {
            .point-dot {
              border-color: #3b82f6;
            }
            .point-year, .point-info {
              opacity: 1;
            }
          }

          &.active {
            .point-dot {
              background: #3b82f6;
              border-color: #3b82f6;
              box-shadow: 0 0 0 4px #3b82f630;
            }

            .point-year {
              color: #3b82f6;
              opacity: 1;
            }

            .point-info {
              opacity: 1;
            }
          }
        }
      }
    }
  }

  /* Header Actions */
  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .sort-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: transparent;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 0.72em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    ${mono};
    letter-spacing: 0.03em;

    svg {
      font-size: 0.9em;
      opacity: 0.7;
    }

    &:hover {
      opacity: 0.9;
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    &:active {
      transform: scale(0.98);
    }
  }

  /* Grid de Histórico lado a lado */
  .history-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    height: 100%;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  /* ====== HISTÓRICO DE VENDAS / COMPRAS — CLONE DA PRODUCTS-TABLE ====== */
  .history-table-wrapper {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;

    .history-header-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      background: ${({ theme }) => surface(theme, 0)};

      .header-title-group {
        display: flex;
        align-items: center;
        gap: 10px;

        .header-icon {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: ${tokens.radiusMd};
          background: ${({ theme }) => surface(theme, 1)};
          color: ${({ theme }) => theme.text};
          opacity: 0.6;

          svg { font-size: 0.85em; }
        }

        h4 {
          margin: 0;
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
        }
      }

      .header-meta {
        display: flex;
        align-items: center;
        gap: 12px;

        .history-count {
          font-size: 11px;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  .history-table {
    padding: 0;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.border};
      border-radius: 2px;
    }

    .table-header {
      display: grid;
      grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) auto;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => theme.tableRow};
      border-bottom: 1px solid ${({ theme }) => theme.border};
      font-size: 11px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      position: sticky;
      top: 0;
      z-index: 2;
      align-items: center;

      > * {
        text-align: left;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      > *:last-child { text-align: right; }
    }

    .table-row {
      display: grid;
      grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) auto;
      gap: 12px;
      min-width: 0;
      padding: 16px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.border}20;
      transition: all 0.2s ease;
      align-items: center;

      &:hover {
        background: ${({ theme }) => theme.tableRowHover};
      }

      &:last-child {
        border-bottom: none;
      }

      > * {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        min-width: 0;
        overflow: hidden;
      }
      > *:last-child {
        justify-content: flex-end;
      }

      .product-info {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
        width: 100%;

        .product-color {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .product-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;

          .product-name {
            font-size: 13px;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .product-code {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }

      .quantity-badge {
        font-size: 12px;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;
      }

      .value-text {
        font-size: 13px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      .value-highlight {
        font-size: 13px;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
      }

      .download-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 32px;
        height: 24px;
        padding: 0 8px;
        background: ${({ theme }) => theme.buttonBg};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
        cursor: pointer;
        transition: all 0.2s ease;

        svg { font-size: 12px; }

        &:hover {
          opacity: 1;
          background: ${({ theme }) => theme.tableRowHover};
          border-color: ${({ theme }) => theme.primary};
          color: ${({ theme }) => theme.primary};
        }
      }
    }

    .history-empty {
      padding: 56px 20px;
      text-align: center;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-size: 13px;
      font-weight: 400;
    }

    .history-loader {
      padding: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-size: 13px;

      .spinner {
        width: 14px;
        height: 14px;
        border: 2px solid ${({ theme }) => theme.border};
        border-top-color: ${({ theme }) => theme.primary};
        border-radius: 50%;
        animation: ${spin} 0.8s linear infinite;
      }
    }
  }

  /* Spinner animation */
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* Conteúdo da Reforma */
  .reform-info-content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .reform-info-item {
      padding: 16px;
      background: ${({ theme }) => theme.tableRow};
      border-radius: 12px;
      border-left: 3px solid ${({ theme }) => theme.primary};

      h5 {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        margin: 0 0 8px 0;
      }

      p {
        font-size: 13px;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;
        margin: 0;
        line-height: 1.6;
      }
    }
  }
`;
